import { useCallback, useState } from "react";
import { MapPin } from "lucide-react";
import { useShareLocationForm } from "@/components/site/shareLocation/useShareLocationForm";
import ShareLocationDialog from "@/components/site/shareLocation/ShareLocationDialog";

/**
 * ShareLocationButton
 * Trigger button + modal for the "Request Emergency Tow" flow.
 * All state/business logic lives in useShareLocationForm; the modal shell
 * lives in ShareLocationDialog. This file only owns:
 *   - the trigger button and its variant styling
 *   - the open/close boolean
 *
 * Props:
 *   variant: "primary" | "ghost" | "mobile" | "luxury"
 *   label: button text
 *   className: extra classes on the trigger
 *   testId: base data-testid (sub-elements append suffixes)
 */

const TRIGGER_BASE =
  "inline-flex items-center justify-center gap-2 font-display font-black uppercase tracking-wider transition-colors";

// Lookup map replaces the previous 4-way nested ternary — one place to add
// new visual variants without cyclomatic-complexity growth.
const TRIGGER_VARIANTS = {
  primary:
    "bg-white text-slate-900 border-2 border-black hover:bg-slate-100 px-6 py-4 text-base bm-hard-shadow hover:translate-x-1 hover:translate-y-1 hover:shadow-none min-h-[56px]",
  ghost:
    "bg-transparent text-white border-2 border-white hover:bg-white/10 px-6 py-4 text-base min-h-[56px]",
  mobile:
    "lux-glass text-white border border-white/20 hover:border-yellow-400/50 px-3 py-3 text-xs rounded-full min-h-[52px]",
  luxury:
    "lux-glass text-white border border-yellow-400/40 hover:border-yellow-400 hover:text-yellow-400 px-6 py-4 text-base rounded-full min-h-[56px]",
};

export default function ShareLocationButton({
  variant = "primary",
  className = "",
  testId = "share-location",
  label = "Share My Location",
}) {
  const [open, setOpen] = useState(false);
  const controller = useShareLocationForm();

  const openModal = useCallback(() => {
    controller.reset();
    setOpen(true);
  }, [controller]);

  const closeModal = useCallback(() => setOpen(false), []);

  const variantClasses = TRIGGER_VARIANTS[variant] || "";

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid={testId}
        className={`${TRIGGER_BASE} ${variantClasses} ${className}`}
        aria-label={label}
      >
        <MapPin className="h-5 w-5" strokeWidth={3} />
        {label}
      </button>

      {open && (
        <ShareLocationDialog
          testId={testId}
          onClose={closeModal}
          controller={controller}
        />
      )}

      <style>{`
        .bm-input {
          width: 100%;
          background: #0f172a;
          border: 2px solid #334155;
          color: #f1f5f9;
          padding: 12px 14px;
          font-size: 15px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.15s;
        }
        .bm-input:focus { border-color: #fbbf24; }
        .bm-input::placeholder { color: #64748b; }
      `}</style>
    </>
  );
}
